import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, PaperPlaneRight, FileText, ArrowCounterClockwise, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["Pending", "Approved", "Rejected", "Forwarded to DEXT"];
const DOC_TYPES = ["Invoice", "Receipt", "Bank Statement", "Purchase Order", "Contract", "Credit Note", "Expense", "Other"];
const typeStyles = {
  "Invoice": "bg-indigo-50 border-indigo-200 text-indigo-700",
  "Receipt": "bg-teal-50 border-teal-200 text-teal-700",
  "Bank Statement": "bg-cyan-50 border-cyan-200 text-cyan-700",
  "Purchase Order": "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
  "Contract": "bg-slate-100 border-slate-300 text-slate-700",
  "Credit Note": "bg-orange-50 border-orange-200 text-orange-700",
  "Expense": "bg-rose-50 border-rose-200 text-rose-700",
  "Other": "bg-slate-50 border-slate-200 text-slate-600",
};
const statusStyles = {
  "Pending": "bg-amber-50 border-amber-200 text-amber-700",
  "Approved": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "Rejected": "bg-red-50 border-red-200 text-red-700",
  "Forwarded to DEXT": "bg-blue-50 border-blue-200 text-blue-700",
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ file_name: "", client_id: "", document_type: "Invoice", size_kb: 120 });
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState("active"); // active | archived
  const navigate = useNavigate();

  const load = async () => {
    const [d, c] = await Promise.all([
      api.get(`/documents?archived=${view === "archived"}`),
      api.get("/clients"),
    ]);
    setDocs(d.data); setClients(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));

  const create = async () => {
    if (!form.file_name) return;
    await api.post("/documents", {
      file_name: form.file_name,
      client_id: form.client_id || null,
      document_type: form.document_type || "Invoice",
      size_kb: Number(form.size_kb) || null,
    });
    setForm({ file_name: "", client_id: "", document_type: "Invoice", size_kb: 120 }); setOpen(false);
    toast.success("Document added"); load();
  };
  const setStatus = async (id, status) => {
    await api.patch(`/documents/${id}`, { status });
    toast.success(status === "Forwarded to DEXT" ? "Forwarded & archived" : `Marked ${status}`);
    load();
  };
  const setClient = async (id, client_id) => {
    await api.patch(`/documents/${id}`, { client_id });
    toast.success("Client linked"); load();
  };
  const setDocType = async (id, document_type) => {
    await api.patch(`/documents/${id}`, { document_type });
    toast.success("Type updated"); load();
  };
  const sendDext = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/documents/${id}/send-to-dext`);
      toast.success(`Sent to ${r.data.to} · archived`);
      load();
    } finally { setBusy(null); }
  };
  const restore = async (id) => {
    await api.post(`/documents/${id}/restore`);
    toast.success("Restored to active"); load();
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Documents</div>
          <h1 className="mt-1 font-[Manrope] font-extrabold text-4xl tracking-tight">Document dashboard</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">Track every original file, its status, and its onward journey to DEXT.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-doc-btn" className="bg-[#0F172A] hover:bg-[#0F172A]/90 text-white"><Plus size={16} className="mr-1.5"/> Add document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Register a document</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="doc-name" placeholder="invoice_2026-02_acme.pdf" value={form.file_name} onChange={e => setForm({ ...form, file_name: e.target.value })} />
              <Select value={form.document_type} onValueChange={v => setForm({ ...form, document_type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue placeholder="Document type" /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="doc-client"><SelectValue placeholder="Link to client (optional)" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="doc-size" type="number" placeholder="Size (KB)" value={form.size_kb} onChange={e => setForm({ ...form, size_kb: e.target.value })} />
            </div>
            <DialogFooter><Button data-testid="doc-save" onClick={create}>Register</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={view} onValueChange={setView} data-testid="doc-view-tabs">
        <TabsList>
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">
            <Archive size={13} className="mr-1.5" /> Archived
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border border-slate-200 shadow-none rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/60">
              <TableHead>File name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-slate-500">No documents. Register your first file.</TableCell></TableRow>}
            {docs.map(d => {
              const linkedClient = d.client_id ? clientMap[d.client_id] : null;
              const dextEmail = linkedClient?.dext_email || "inbox@dext.placeholder";
              return (
              <TableRow key={d.id} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`doc-row-${d.id}`}
                onClick={(e) => { if (e.target.closest("button, [role=combobox], [role=option]")) return; navigate(`/documents/${d.id}`); }}>
                <TableCell>
                  <button data-testid={`open-doc-${d.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/documents/${d.id}`); }} className="flex items-center gap-2 text-left hover:text-blue-700">
                    <FileText size={16} className="text-slate-500" />
                    <div>
                      <div className="font-semibold text-sm">{d.file_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{d.size_kb ? `${d.size_kb} KB` : ""}</div>
                    </div>
                  </button>
                </TableCell>
                <TableCell>
                  <Select value={d.document_type || "Invoice"} onValueChange={v => setDocType(d.id, v)}>
                    <SelectTrigger data-testid={`doc-type-${d.id}`} className={`w-[160px] border ${typeStyles[d.document_type || "Invoice"]}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={d.client_id || ""} onValueChange={v => setClient(d.id, v)}>
                    <SelectTrigger data-testid={`doc-client-${d.id}`} className="w-[200px]"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {linkedClient?.dext_email && (
                    <div className="mt-1 text-[10px] text-slate-500 font-mono truncate max-w-[200px]" title={linkedClient.dext_email}>→ {linkedClient.dext_email}</div>
                  )}
                </TableCell>
                <TableCell>
                  <Select value={d.status} onValueChange={v => setStatus(d.id, v)}>
                    <SelectTrigger data-testid={`doc-status-${d.id}`} className={`w-[180px] border ${statusStyles[d.status]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{new Date(d.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    {view === "archived" ? (
                      <Button
                        data-testid={`restore-${d.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => restore(d.id)}
                      >
                        <ArrowCounterClockwise size={14} className="mr-1.5" /> Restore
                      </Button>
                    ) : (
                      <Button
                        data-testid={`send-to-dext-${d.id}`}
                        size="sm"
                        disabled={busy === d.id || d.status === "Forwarded to DEXT"}
                        onClick={() => sendDext(d.id)}
                        title={`Send to ${dextEmail}`}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <PaperPlaneRight size={14} weight="fill" className="mr-1.5"/> {busy === d.id ? "Sending…" : "Send to DEXT"}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
